/** The Site Register's calendar, opened from whatever trigger its host draws:
 *  the day pill in the global header, or the page's own toolbar in the native
 *  shells, where the web header is not shown. Days after today are disabled
 *  because nobody can have signed in on them yet. */

import { useRef, useState, type ReactNode } from 'react';
import { Calendar } from '../../../shared/ui/calendar';
import { Popover, PopoverAnchor, PopoverContent } from '../../../shared/ui/popover';
import { dateKeyToPickerDate, pickerDateToDateKey } from './siteRegisterUtils';

interface SiteRegisterDayPickerProps {
  dayKey: string;
  todayKey: string;
  onSelectDay: (dayKey: string) => void;
  /** Draws the trigger. `toggle` opens and closes the calendar. */
  renderTrigger: (toggle: () => void, open: boolean) => ReactNode;
}

export function SiteRegisterDayPicker({ dayKey, todayKey, onSelectDay, renderTrigger }: SiteRegisterDayPickerProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <>
      <span ref={anchorRef} className="inline-flex">
        {renderTrigger(() => setOpen((previous) => !previous), open)}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        {/* A virtual anchor rather than PopoverTrigger: the header's pill is
            its own motion.button, which Radix's Slot cannot wrap. */}
        <PopoverAnchor virtualRef={anchorRef as never} />
        <PopoverContent
          surface="solid"
          align="end"
          sideOffset={6}
          className="w-auto p-0"
          // The trigger sits outside the popover, so without this a click on
          // it would close the calendar and the same click would reopen it.
          onInteractOutside={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
        >
          <Calendar
            mode="single"
            required
            autoFocus
            selected={dateKeyToPickerDate(dayKey)}
            defaultMonth={dateKeyToPickerDate(dayKey)}
            endMonth={dateKeyToPickerDate(todayKey)}
            disabled={{ after: dateKeyToPickerDate(todayKey) }}
            onSelect={(date: Date) => {
              onSelectDay(pickerDateToDateKey(date));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </>
  );
}
